import PremiumHeader from "@/components/premium/PremiumHeader";
import SEO from "@/components/SEO";
import PremiumFooter from "@/components/premium/PremiumFooter";
import StickyContactBar from "@/components/premium/StickyContactBar";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Building2, Briefcase, Wifi, Car, MapPin, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const CorporateHousingPage = () => {
  const benefits = [
    "Fully furnished executive residence",
    "High-speed WiFi for remote work",
    "Dedicated workspace options",
    "Minutes to L3Harris, Northrop Grumman",
    "Close to Melbourne business district",
    "Private parking for multiple vehicles",
    "Flexible lease terms",
    "Direct invoicing available",
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title="Corporate Housing Melbourne FL | Furnished Executive Beach Rental Near L3Harris"
        description="Executive furnished corporate housing near L3Harris, Northrop Grumman & downtown Melbourne, FL. Luxury 4BR beach house in Indialantic for 3–9 month stays. Book direct and save."
        path="/corporate-housing"
      />
      <PremiumHeader />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://customer-assets.emergentagent.com/job_0254b5f2-ccc0-452f-a975-e862c02dbf90/artifacts/anzmkzaa_IMG_7032.jpeg"
              alt="Corporate Housing"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#0A1628]/70" />
          </div>
          <div className="relative z-10 container-premium pb-20 pt-40">
            <div className="gold-line mb-6" />
            <span className="label-sm text-[#C9A962]">Corporate Housing</span>
            <h1 className="headline-xl text-white mt-4 mb-6 max-w-3xl">
              Executive Housing for Business Relocations
            </h1>
            <p className="body-lg text-white/80 max-w-2xl mb-8">
              Premium furnished residence for corporate executives, project teams, and business relocations. 
              Minutes from Space Coast employers.
            </p>
            <Link to="/contact">
              <Button className="bg-[#C9A962] text-[#0A1628] hover:bg-[#B8954D] rounded-none px-8 py-6 text-base">
                Request Corporate Quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Benefits */}
        <section className="section-lg">
          <div className="container-premium">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="headline-lg text-[#0A1628] mb-6">Built for Business</h2>
                <p className="body-md text-[#0A1628]/60 mb-8">
                  Whether you're relocating for a new role, managing a project, or need temporary housing 
                  during a corporate transition, our residence offers the comfort and convenience executives expect.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {benefits.map((b, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-center gap-3"
                    >
                      <Check className="w-4 h-4 text-[#C9A962]" />
                      <span className="text-sm text-[#0A1628]/70">{b}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#0A1628] p-8 text-center">
                  <Building2 className="w-8 h-8 text-[#C9A962] mx-auto mb-4" />
                  <p className="font-display text-2xl text-white mb-1">15 min</p>
                  <p className="text-sm text-white/60">to L3Harris</p>
                </div>
                <div className="bg-[#F7F3ED] p-8 text-center">
                  <Briefcase className="w-8 h-8 text-[#0A1628] mx-auto mb-4" />
                  <p className="font-display text-2xl text-[#0A1628] mb-1">10 min</p>
                  <p className="text-sm text-[#0A1628]/60">to Melbourne CBD</p>
                </div>
                <div className="bg-[#F7F3ED] p-8 text-center">
                  <Wifi className="w-8 h-8 text-[#0A1628] mx-auto mb-4" />
                  <p className="font-display text-2xl text-[#0A1628] mb-1">Fast</p>
                  <p className="text-sm text-[#0A1628]/60">WiFi Included</p>
                </div>
                <div className="bg-[#0A1628] p-8 text-center">
                  <Car className="w-8 h-8 text-[#C9A962] mx-auto mb-4" />
                  <p className="font-display text-2xl text-white mb-1">3+</p>
                  <p className="text-sm text-white/60">Parking Spots</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#0A1628]">
          <div className="container-premium text-center">
            <h3 className="headline-md text-white mb-4">Need Corporate Housing?</h3>
            <p className="body-md text-white/60 mb-8 max-w-xl mx-auto">
              Contact us for corporate rates, direct invoicing, and flexible terms for your business needs.
            </p>
            <Link to="/contact">
              <Button className="bg-[#C9A962] text-[#0A1628] hover:bg-[#B8954D] rounded-none px-8 py-4">
                Get Corporate Quote
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <PremiumFooter />
      <StickyContactBar />
    </div>
  );
};

export default CorporateHousingPage;
